// contexts/MentionContext.tsx

import React, {createContext, useContext, useState, ReactNode, useEffect} from "react";
import { WorkspaceMember } from "../../../lib/types";
import {BaseRange, Editor, Range} from "slate";
import {ReactEditor} from "slate-react";
import {insertMention} from "./mentions";
import {useUserContext} from "../../../providers/UserContext";

interface MentionContextType {
  target: BaseRange | undefined;
  setTarget: (target: BaseRange | undefined) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  mentionResults: WorkspaceMember[];
  setMentionResults: (results: WorkspaceMember[]) => void;
  mentionIndex: number;
  setMentionIndex: (index: number) => void;
  updateSearch: (editor: ReactEditor) => void;
  handleMentionKeyDown: (editor: ReactEditor, event: React.KeyboardEvent) => boolean;
  members: WorkspaceMember[];
}

const MentionContext = createContext<MentionContextType | undefined>(undefined);

export const MentionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<BaseRange | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [mentionResults, setMentionResults] = useState<WorkspaceMember[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const {activeWorkspace} = useUserContext();
  const members = Object.values(activeWorkspace?.members || {});

  const updateSearch = (editor: ReactEditor) => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);
      const wordBefore = Editor.before(editor, start, { unit: "word" });
      const before = wordBefore && Editor.before(editor, wordBefore);
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/^@(\w*)$/);

      if (beforeMatch) {
        setTarget(beforeRange);
        setSearchTerm(beforeMatch[1]);
        setMentionIndex(0);
        return;
      }
    }
    setTarget(undefined);
    setSearchTerm("");
  };

  useEffect(() => {
    if (!target) {
      setMentionResults([]);
      return;
    }
    const filteredResults = members.filter((member) =>
      member.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => a.displayName.toLowerCase().indexOf(searchTerm.toLowerCase()) - b.displayName.toLowerCase().indexOf(searchTerm.toLowerCase()));
    setMentionResults(filteredResults.slice(0, 5));
  }, [searchTerm, setMentionResults, target]);


  const handleMentionKeyDown = (editor: ReactEditor, event: React.KeyboardEvent): boolean => {
    if (!mentionResults.length) return false; // Skip if no mentions are available

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setMentionIndex((prev) => (prev + 1) % mentionResults.length);
        return true;
      case "ArrowUp":
        event.preventDefault();
        setMentionIndex((prev) => (prev - 1 + mentionResults.length) % mentionResults.length);
        return true;
      case "Enter":
        event.preventDefault();
        const selectedMention = mentionResults[mentionIndex];
        if (selectedMention) {
          insertMention(editor, selectedMention);
          setSearchTerm("");
          setMentionResults([]);
        }
        return true;
      case "Escape":
        event.preventDefault();
        setSearchTerm("");
        setMentionResults([]);
        return true;
      default:
        return false; // Allow other handlers to process
    }
  };

  return (
    <MentionContext.Provider
      value={{
        target,
        setTarget,
        searchTerm,
        setSearchTerm,
        mentionResults,
        setMentionResults,
        mentionIndex,
        setMentionIndex,
        updateSearch,
        handleMentionKeyDown,
        members
      }}
    >
      {children}
    </MentionContext.Provider>
  );
};

export const useMentionContext = (): MentionContextType => {
  const context = useContext(MentionContext);
  if (!context) {
    throw new Error("useMentionContext must be used within a MentionProvider");
  }
  return context;
};